import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Alert, Badge, ProgressBar, Table } from 'react-bootstrap';
import { subscriptionApi } from '../services/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiCheck, FiX, FiZap, FiCalendar, FiUsers, FiBarChart, FiMail, FiSmartphone, FiGlobe, FiCreditCard } from 'react-icons/fi';

const SubscriptionPage = () => {
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Check if redirected from successful checkout FIRST
    const sessionId = searchParams.get('session_id');
    console.log('SubscriptionPage useEffect - sessionId:', sessionId, 'searchParams:', searchParams.toString());
    
    if (sessionId) {
      // Verify the session and create subscription if needed
      const verifyAndRefresh = async () => {
        try {
          console.log('=== Starting verification process ===');
          console.log('Session ID:', sessionId);
          console.log('Calling verifySession API...');
          
          const response = await subscriptionApi.verifySession(sessionId);
          console.log('Verification response received:', response);
          console.log('Response data:', response.data);
          
          if (response.data.success) {
            console.log('✓ Subscription verified successfully');
            console.log('Subscription data:', response.data.data);
            console.log('Plan:', response.data.data.plan?.displayName || response.data.data.subscription?.plan?.displayName);
            
            // If we got subscription data back, update state immediately
            if (response.data.data.subscription || response.data.data.plan) {
              console.log('Updating subscription state from verification response');
              setCurrentSubscription({
                subscription: response.data.data.subscription,
                plan: response.data.data.plan || response.data.data.subscription?.plan,
                isActive: response.data.data.isActive !== false
              });
            }
          } else {
            console.warn('⚠ Verification returned success: false', response.data);
          }
          
          // Wait a moment for the database to update, then refresh
          setTimeout(() => {
            console.log('Refreshing subscription data...');
            fetchData();
          }, 1500);
        } catch (err) {
          console.error('✗ Session verification error:', err);
          console.error('Error response:', err.response);
          console.error('Error details:', err.response?.data);
          // Still refresh data in case webhook already processed it
          setTimeout(() => {
            console.log('Refreshing subscription data after error...');
            fetchData();
          }, 2000);
        }
      };
      
      // Don't fetch data first if we have a session_id - verify first
      verifyAndRefresh();
    } else {
      // No session_id, just fetch data normally
      fetchData();
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log('Fetching subscription data...');
      const [plansRes, subscriptionRes, usageRes, paymentsRes] = await Promise.all([
        subscriptionApi.getPlans(),
        subscriptionApi.getCurrent(),
        subscriptionApi.getUsage(),
        subscriptionApi.getPaymentHistory()
      ]);

      console.log('Subscription response:', subscriptionRes.data);
      console.log('Current subscription data:', subscriptionRes.data.data);
      
      setPlans(plansRes.data.data.plans);
      setCurrentSubscription(subscriptionRes.data.data);
      setUsage(usageRes.data.data);
      setPaymentHistory(paymentsRes.data.data.payments || []);
      setError('');
      
      console.log('Subscription state updated');
    } catch (err) {
      console.error('Error fetching subscription data:', err);
      setError(err.response?.data?.message || 'Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    try {
      setProcessing(true);
      setError('');
      console.log('Creating checkout session for plan:', planId);
      
      const response = await subscriptionApi.createCheckout(planId);
      console.log('Checkout response:', response);
      
      // Check if subscription was updated immediately (prorated upgrade)
      if (response.data?.data?.subscription) {
        console.log('Subscription updated immediately, refreshing data...');
        // Subscription was updated directly (prorated), refresh data
        await fetchData();
        setProcessing(false);
        return;
      }
      
      // Redirect to Stripe Checkout for new subscriptions
      if (response.data?.data?.url) {
        window.location.href = response.data.data.url;
      } else {
        setError('No checkout URL received from server');
        setProcessing(false);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create checkout session';
      setError(errorMessage);
      setProcessing(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? It will remain active until the end of the billing period.')) {
      return;
    }

    try {
      await subscriptionApi.cancel();
      await fetchData();
      alert('Subscription will be cancelled at the end of the billing period.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to cancel subscription');
    }
  };

  const handleReactivate = async () => {
    try {
      await subscriptionApi.reactivate();
      await fetchData();
      alert('Subscription reactivated successfully!');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reactivate subscription');
    }
  };

  const handleManageBilling = async () => {
    try {
      const response = await subscriptionApi.getCustomerPortal();
      if (response.data.data.url) {
        window.location.href = response.data.data.url;
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to open billing portal');
    }
  };

  const getFeatureIcon = (feature) => {
    const icons = {
      basic_booking: <FiCalendar />,
      email_notifications: <FiMail />,
      sms_notifications: <FiSmartphone />,
      calendar_sync: <FiCalendar />,
      custom_branding: <FiZap />,
      advanced_analytics: <FiBarChart />,
      custom_domain: <FiGlobe />,
      api_access: <FiZap />,
      priority_support: <FiUsers />
    };
    return icons[feature] || <FiCheck />;
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="loading-spinner" style={{ margin: '0 auto', width: '40px', height: '40px' }}></div>
        <p className="mt-3">Loading subscription information...</p>
      </div>
    );
  }

  const isActive = currentSubscription?.isActive;
  const currentPlan = currentSubscription?.plan || currentSubscription?.subscription?.plan;
  
  console.log('Rendering subscription page:', {
    currentSubscription,
    isActive,
    currentPlan: currentPlan?.displayName,
    hasSubscription: !!currentSubscription?.subscription
  });

  return (
    <div className="subscription-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Subscription & Billing</h2>
        {isActive && currentSubscription?.subscription?.stripeCustomerId && (
          <Button variant="outline-primary" onClick={handleManageBilling}>
            <FiCreditCard className="me-2" />
            Manage Billing
          </Button>
        )}
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      {searchParams.get('session_id') && (
        <Alert variant="success">
          Payment successful! Your subscription is now active.
        </Alert>
      )}

      {/* Current Subscription Status */}
      {(currentSubscription && (currentSubscription.plan || currentSubscription.subscription)) && (
        <Card className="mb-4 border-primary">
          <Card.Header className="bg-primary text-white">
            <h5 className="mb-0">Current Plan</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <h4>{currentPlan?.displayName || currentSubscription?.plan?.displayName || 'Free'}</h4>
                <p className="text-muted">{currentPlan?.description || currentSubscription?.plan?.description || 'Free tier'}</p>
                {currentSubscription.subscription && (
                  <div className="mt-3">
                    {currentSubscription.subscription.currentPeriodEnd && (
                      <p className="mb-1">
                        <strong>Renews:</strong>{' '}
                        {new Date(currentSubscription.subscription.currentPeriodEnd).toLocaleDateString()}
                      </p>
                    )}
                    {currentSubscription.subscription.cancelAtPeriodEnd && (
                      <Badge bg="warning" className="mb-2">
                        Cancels at period end
                      </Badge>
                    )}
                  </div>
                )}
              </Col>
              <Col md={6} className="text-end">
                {currentSubscription.subscription?.cancelAtPeriodEnd ? (
                  <Button variant="success" onClick={handleReactivate}>
                    Reactivate Subscription
                  </Button>
                ) : currentPlan?.name !== 'free' ? (
                  <Button variant="danger" onClick={handleCancel}>
                    Cancel Subscription
                  </Button>
                ) : null}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Usage Statistics */}
      {usage && usage.usage && usage.usage.seats && (
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Seat Usage</h5>
          </Card.Header>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h4 className="mb-0">
                  {usage.usage.seats.current} / {usage.usage.seats.isUnlimited ? '∞' : usage.usage.seats.limit} Seats
                </h4>
                <p className="text-muted small mb-0">
                  {usage.usage.seats.isUnlimited 
                    ? 'Unlimited seats available' 
                    : `${usage.usage.seats.limit - usage.usage.seats.current} seat${usage.usage.seats.limit - usage.usage.seats.current === 1 ? '' : 's'} remaining`}
                </p>
              </div>
              {!usage.usage.seats.isUnlimited && (
                <div className="text-end">
                  <div className="fs-3 fw-bold">{usage.usage.seats.percentage}%</div>
                  <div className="text-muted small">Used</div>
                </div>
              )}
            </div>
            {!usage.usage.seats.isUnlimited && (
              <ProgressBar
                now={usage.usage.seats.percentage}
                variant={usage.usage.seats.percentage > 80 ? 'danger' : usage.usage.seats.percentage > 60 ? 'warning' : 'success'}
                style={{ height: '10px' }}
              />
            )}
            {usage.usage.seats.percentage >= 80 && !usage.usage.seats.isUnlimited && (
              <Alert variant="warning" className="mt-3 mb-0">
                You're running low on seats. Consider upgrading to add more team members.
              </Alert>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Subscription Plans */}
      <h4 className="mb-3">Available Plans</h4>
      <Row>
        {plans.map((plan) => {
          const isCurrentPlan = currentPlan?.id === plan.id;

          return (
            <Col md={6} lg={3} key={plan.id} className="mb-4">
              <Card className={`h-100 ${isCurrentPlan ? 'border-primary shadow' : ''}`}>
                {isCurrentPlan && (
                  <Card.Header className="bg-primary text-white text-center">
                    <Badge bg="light" text="dark">Current Plan</Badge>
                  </Card.Header>
                )}
                <Card.Body className="d-flex flex-column">
                  <div className="text-center mb-3">
                    <h4>{plan.displayName}</h4>
                    <h3 className="text-primary">
                      ${parseFloat(plan.price || 0).toFixed(2)}
                      <small className="text-muted fs-6">/{plan.billingCycle === 'yearly' ? 'yr' : 'mo'}</small>
                    </h3>
                    <p className="text-muted small">{plan.description}</p>
                  </div>

                  <div className="mb-3 flex-grow-1">
                    <div className="mb-3">
                      <div className="d-flex align-items-center justify-content-center mb-2">
                        <FiUsers className="me-2 text-primary" size={20} />
                        <strong className="fs-5">
                          {plan.maxStaffMembers === -1 ? 'Unlimited' : plan.maxStaffMembers} {plan.maxStaffMembers === 1 ? 'Seat' : 'Seats'}
                        </strong>
                      </div>
                      <p className="text-muted small text-center mb-0">
                        {plan.maxStaffMembers === -1 
                          ? 'Add as many team members as you need' 
                          : `Up to ${plan.maxStaffMembers} team member${plan.maxStaffMembers === 1 ? '' : 's'}`}
                      </p>
                    </div>

                  </div>

                  <Button
                    variant={isCurrentPlan ? 'outline-primary' : 'primary'}
                    className="mt-auto"
                    disabled={isCurrentPlan || processing}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('Button clicked for plan:', plan.id, plan);
                      handleUpgrade(plan.id);
                    }}
                  >
                    {processing ? 'Processing...' : isCurrentPlan ? 'Current Plan' : parseFloat(plan.price || 0) === 0 ? 'Get Started' : 'Upgrade'}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* Payment History */}
      {paymentHistory.length > 0 && (
        <Card className="mt-4">
          <Card.Header>
            <h5 className="mb-0">Payment History</h5>
          </Card.Header>
          <Card.Body>
            <Table striped hover>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Plan</th>
                </tr>
              </thead>
              <tbody>
                {paymentHistory.map((payment) => (
                  <tr key={payment.id}>
                    <td>{new Date(payment.createdAt).toLocaleDateString()}</td>
                    <td>${parseFloat(payment.amount || 0).toFixed(2)} {payment.currency || 'USD'}</td>
                    <td>
                      <Badge bg={payment.status === 'succeeded' ? 'success' : payment.status === 'failed' ? 'danger' : 'warning'}>
                        {payment.status}
                      </Badge>
                    </td>
                    <td>{payment.subscription?.plan?.displayName || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default SubscriptionPage;
